import { z } from 'zod';

import { useStorage } from '@@hooks/useStorage';
import { type ContentLanguage, LANG_CODES } from '@@lib/i18n/constants';
import { useContentLocale } from '@@scripts/store/settings/useSettingsStore';

type PersistedPlacementColumns = {
    [LANG_CODES.GERMAN]: string[];
    [LANG_CODES.FRENCH]: string[];
};

export const PERSISTED_COLUMNS_KEY = 'placement_columns';

const persistedColumnsSchema = z.record(z.string(), z.array(z.string())).catch({});

/** The columns the user left open in a content language, for code outside React such as a route */
export const getPersistedColumns = (contentLocale: ContentLanguage) => {
    try {
        const stored: unknown = JSON.parse(localStorage.getItem(PERSISTED_COLUMNS_KEY) ?? '{}');

        return persistedColumnsSchema.parse(stored)[contentLocale] ?? [];
    } catch {
        // Not JSON, so nothing to restore
        return [];
    }
};

export const useSetPersistedColumns = () => {
    const contentLocale = useContentLocale();

    const [persistedColumns, setPersistedColumns] = useStorage<PersistedPlacementColumns>(
        PERSISTED_COLUMNS_KEY,
        {
            [LANG_CODES.GERMAN]: [],
            [LANG_CODES.FRENCH]: [],
        },
    );

    return (columns: string[]) => {
        setPersistedColumns({
            ...persistedColumns,
            [contentLocale]: columns,
        });
    };
};
